import { IntegrationType, NonSensitiveFlightRecordConnectedAccount } from "@nova/schemas"
import { ConnectedAccountIntegrationSection } from "./ConnectedAccountIntegrationSection"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { BeforeYouSyncModalSkydio } from "../modals/sync/BeforeYouSyncModalSkydio"
import { useCallback } from "react"
import * as Icons from "@nova/component-icons"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { ConnectedAccountEditSkydioModal } from "../modals/accounts/edit/ConnectedAccountEditSkydioModal"
import { ConnectedAccountDeleteSkydioModal } from "../modals/accounts/delete/ConnectedAccountDeleteSkydioModal"
import { usePilotTrackingConnectionsContext } from "features/data-management/pilot-tracking/PilotTrackingConnectionsContext"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

export const ConnectedAccountIntegrationSectionSkydio = ({
    accounts,
}: {
    accounts: NonSensitiveFlightRecordConnectedAccount<IntegrationType.skydio>[]
}) => {
    const dispatch = useAppDispatch()
    const confirmationModal = useConfirmationModalContext()
    const { triggerSkydioSync, updateSkydioConnection, deleteSkydioConnection } =
        usePilotTrackingConnectionsContext()

    const showSyncModal = async (id: string) => {
        try {
            await confirmationModal.confirm({
                RenderComponent: BeforeYouSyncModalSkydio,
                asyncConfirmCallback: () => triggerSkydioSync(id),
            })
        } catch (error) {
            console.error(error)

            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to start sync.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    const showEditModal = useCallback(
        async (account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.skydio>) => {
            void confirmationModal.confirm({
                RenderComponent: (props) => (
                    <ConnectedAccountEditSkydioModal
                        {...props}
                        account={account}
                        onSave={updateSkydioConnection}
                        onDelete={() => deleteSkydioConnection(account.id)}
                    />
                ),
            })
        },
        [confirmationModal, updateSkydioConnection, deleteSkydioConnection]
    )

    const showDeleteModal = useCallback(
        async (account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.skydio>) => {
            await confirmationModal.confirm({
                RenderComponent: (props) => (
                    <ConnectedAccountDeleteSkydioModal
                        {...props}
                        account={account}
                        onDelete={() => deleteSkydioConnection(account.id)}
                    />
                ),
            })
        },
        [confirmationModal, deleteSkydioConnection]
    )

    const createContextMenuSections = useCallback(
        (
            account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.skydio>
        ): ContextMenuItem[][] => {
            return [
                [
                    {
                        title: "Edit Details",
                        Icon: Icons.Pencil,
                        onClick: () => showEditModal(account),
                    },
                ],
                [
                    {
                        title: "Disconnect Account",
                        Icon: Icons.LinkOff,
                        onClick: () => showDeleteModal(account),
                        isDestructive: true,
                    },
                ],
            ] satisfies ContextMenuItem[][]
        },
        [showEditModal, showDeleteModal]
    )

    return (
        <ConnectedAccountIntegrationSection
            type={IntegrationType.skydio}
            accounts={accounts}
            onSync={showSyncModal}
            createContextMenuSections={createContextMenuSections}
        />
    )
}
